import i18n from '@/utils/i18n';
import React from 'react';
import {
  APP_CONTAINER_ROUTES,
  APP_ROUTES,
  CREDIT_REQUEST_INVOICE_ROUTES,
  ORDER_ROUTES,
} from '@/navigators/routes';
import {
  AppContainerStackParams,
  AppStackParams,
  CreditRequestInvoiceStackParams,
  OrdersStackParams,
} from '@/navigators/types';
import { CompositeNavigationProp, RouteProp } from '@react-navigation/native';
import { CreditRequestInvoiceDetailsContent } from '@/components/credit-request/credit-request-invoice-details-content';
import { PageContainer } from '@/components/page-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { Type } from '@/components/toast';
import { useCreateCreditRequest } from '@/hooks/use-create-credit-request';
import {
  useCreditRequestInvoiceStore,
  useCreditRequestInvoiceStoreApi,
} from '@/stores/credit-request-invoice-store';
import { useLayoutContext } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';

type Navigation = CompositeNavigationProp<
  StackNavigationProp<
    CreditRequestInvoiceStackParams,
    CREDIT_REQUEST_INVOICE_ROUTES.CreditRequestInvoiceDetails
  >,
  CompositeNavigationProp<
    StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES>,
    CompositeNavigationProp<
      StackNavigationProp<AppStackParams, APP_ROUTES>,
      StackNavigationProp<OrdersStackParams, ORDER_ROUTES.Orders>
    >
  >
>;

interface Props {
  navigation: Navigation;
  route: RouteProp<
    CreditRequestInvoiceStackParams,
    CREDIT_REQUEST_INVOICE_ROUTES.CreditRequestInvoiceDetails
  >;
}

export const CreditRequestInvoiceDetailsScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { title, orderInvoiceUUID, invoiceUUID, orderUUID } = route.params;
  const { isSmallLayout } = useLayoutContext();
  const createCreditRequestFromLineItems = useCreateCreditRequest();
  const clearBatchItems = useCreditRequestInvoiceStore(
    (s) => s.clearBatchItems,
  );
  const creditRequestStoreApi = useCreditRequestInvoiceStoreApi();
  const { showToast, showErrorToast } = useToasts();

  const handleSubmit = async () => {
    const batchItems = creditRequestStoreApi.getState().batchItems;
    try {
      await createCreditRequestFromLineItems(batchItems);
      showToast({
        content: i18n.t('credit_request.toast.success'),
        type: Type.POSITIVE,
      });
      if (isSmallLayout) {
        if (orderUUID) {
          navigation.navigate(APP_CONTAINER_ROUTES.OrderDetail, { orderUUID });
        } else if (invoiceUUID) {
          navigation.navigate(APP_CONTAINER_ROUTES.InvoiceDetail, {
            invoiceUUID,
            renderPayable: false,
          });
        }
      } else {
        navigation.navigate(APP_ROUTES.Orders, {
          params: { invoiceUUID, orderUUID },
          screen: ORDER_ROUTES.Orders,
        });
      }
      clearBatchItems();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <PageContainer
      navigationHeaderProps={{
        centerTitle: true,
        responsive: true,
        showBackButton: true,
        showShadow: false,
        title,
      }}
    >
      <CreditRequestInvoiceDetailsContent
        onEmpty={() => navigation.goBack()}
        onSubmit={handleSubmit}
        orderInvoiceUUID={orderInvoiceUUID}
      />
    </PageContainer>
  );
};
